import { useForm } from "@inertiajs/react"
import { AlertCircle, CheckCircle2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"

interface ClaimProfileDialogProps {
  developerId: number
  developerName: string
  variant?: "button" | "link" | "icon"
}

export function ClaimProfileDialog({
  developerId,
  developerName,
  variant = "button",
}: ClaimProfileDialogProps) {
  const [open, setOpen] = useState(false)
  const { data, setData, post, processing, errors, recentlySuccessful } =
    useForm({
      email: "",
      message: "",
    })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/developers/${developerId}/profile_claims`, {
      onSuccess: () => {
        setTimeout(() => {
          setOpen(false)
          setData("email", "")
          setData("message", "")
        }, 2000)
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant === "button" ? (
          <Button variant="outline" size="sm">
            Is this you?
          </Button>
        ) : variant === "icon" ? (
          <button className="hover:bg-primary/10 text-muted-foreground hover:text-primary inline-flex h-8 w-8 items-center justify-center rounded-full font-bold transition-colors">
            <Tooltip>
              <TooltipTrigger asChild>
                <span>!</span>
              </TooltipTrigger>
              <TooltipContent>Claim</TooltipContent>
            </Tooltip>
          </button>
        ) : (
          <button className="text-primary text-sm hover:underline">
            Claim this profile
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Claim This Profile</DialogTitle>
          <DialogDescription>
            Are you {developerName}? Let us know if any information needs to be
            corrected.
          </DialogDescription>
        </DialogHeader>

        {recentlySuccessful ? (
          <div className="flex items-center gap-2 rounded-lg bg-green-50 p-4 text-green-800 dark:bg-green-900/20 dark:text-green-300">
            <CheckCircle2 className="h-5 w-5" />
            <p>Thank you! Your claim has been submitted successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Your Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                disabled={processing}
              />
              {errors.email && (
                <p className="text-destructive flex items-center gap-1 text-sm">
                  <AlertCircle className="h-3 w-3" />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">What would you like to correct?</Label>
              <Textarea
                id="message"
                placeholder="Please describe what information needs to be updated..."
                value={data.message}
                onChange={(e) => setData("message", e.target.value)}
                disabled={processing}
                rows={4}
              />
              {errors.message && (
                <p className="text-destructive flex items-center gap-1 text-sm">
                  <AlertCircle className="h-3 w-3" />
                  {errors.message}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={processing}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? "Submitting..." : "Submit Claim"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
